import { createContext, type ParentComponent, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import type { JobApplication } from "@/domains/application/types";
import type {
  SessionBootstrapCapabilities,
  SessionNextStep,
} from "@/domains/auth/session/types";
import type { CompanyV2 } from "@/domains/company/company/api/types";
import type {
  OnboardingStatus,
  ProfileV2,
} from "@/domains/freelance/profile/api/types";

export type User = {
  name: string;
  email: string;
  password: string | null;
  verificationToken?: string;
  termsAndConsentsAcceptanceDate: Date;
  refreshToken?: string;
};

export type AuthProfile = Pick<
  ProfileV2,
  | "id"
  | "email"
  | "full_name"
  | "username"
  | "onboarding_status"
  | "personal_profile_status"
  | "profile_type"
  | "payment_profile"
  | "assets"
  | "weekly_hours"
  | "available_from"
  | "avatar_url"
  | "last_selected_workspace_id"
  | "billing_status"
> & {
  onboarding_status: OnboardingStatus;
  personal_profile_status: OnboardingStatus;
};

export type AuthStoreV2 = {
  user: User | null;
  profile: AuthProfile | null;
  companies: CompanyV2[] | null;
  user_applications: JobApplication[] | null;
  capabilities: SessionBootstrapCapabilities | null;
  next_step: SessionNextStep | null;
  active_subscription_id: string | null;
  loading: boolean;
};

const createAuthStoreV2 = (initialState: AuthStoreV2) => {
  const DEFAULT_STATE: AuthStoreV2 = {
    user: null,
    profile: null,
    companies: null,
    user_applications: null,
    capabilities: null,
    next_step: null,
    active_subscription_id: null,
    loading: true,
  };
  return createStore<AuthStoreV2>({
    ...DEFAULT_STATE,
    ...initialState,
  });
};

const AuthContextV2 = createContext<ReturnType<typeof createAuthStoreV2>>();

export const AuthProviderV2: ParentComponent<Partial<AuthStoreV2>> = (
  props,
) => {
  const store = createAuthStoreV2({
    user: props.user ?? null,
    profile: props.profile ?? null,
    companies: props.companies ?? null,
    user_applications: props.user_applications ?? null,
    capabilities: props.capabilities ?? null,
    next_step: props.next_step ?? null,
    active_subscription_id: props.active_subscription_id ?? null,
    loading: props.loading ?? true,
  });
  return (
    <AuthContextV2.Provider value={store}>
      {props.children}
    </AuthContextV2.Provider>
  );
};

export const useAuthV2 = () => {
  const store = useContext(AuthContextV2);
  if (!store) {
    throw new Error("useAuthV2 must be used within a AuthProviderV2");
  }
  return store;
};
